import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import imageLibraryAdminApi from '../../services/imageLibraryAdminApi';
import imageLibraryApi from '../../services/imageLibraryApi';
import {
  PageHeader,
  TWInput,
  TWButton,
  TWPagination,
  EmptyState,
  TWConfirmModal,
} from '../../components/ui';
import ImageLibraryGrid from './components/ImageLibraryGrid';
import ImageLibraryUpsertModal from './components/ImageLibraryUpsertModal';
import ImageLibraryAdminSkeleton from './components/ImageLibraryAdminSkeleton';
import './image-library-admin.css';

const cn = (...classes) => classes.filter(Boolean).join(' ');

// Klasör filtresi pill'i — admin galeri ile restoran browse modalını aynı dilde tutar.
const FolderPill = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition',
      active
        ? 'border-blue-600 bg-blue-600 text-white shadow-sm dark:border-sky-500 dark:bg-sky-500 dark:text-slate-900'
        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600',
    )}
  >
    {label}
    {count != null ? (
      <span className={active ? 'opacity-70' : 'text-slate-400'}>
        {' '}
        {count}
      </span>
    ) : null}
  </button>
);

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 400;

const SHELL_CLASS =
  'image-library-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';

const ImageLibraryAdmin = () => {
  const [images, setImages] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    totalRecords: 0,
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [folder, setFolder] = useState('');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [upsertModal, setUpsertModal] = useState({ open: false, image: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Ham arama girdisini debounce ile fetch değerine taşır.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Klasör listesi — restoran tarafıyla aynı endpoint; sayım için hızlı tek seferlik fetch.
  const loadFolders = useCallback(() => {
    imageLibraryApi
      .folders()
      .then((res) => setFolders(res.data?.data || []))
      .catch(() => setFolders([]));
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const fetchImages = useCallback(
    async (pageNumber, searchText, folderValue) => {
      setLoading(true);
      try {
        const searchPart = searchText
          ? `&search=${encodeURIComponent(searchText)}`
          : '';
        const folderPart = folderValue
          ? `&folder=${encodeURIComponent(folderValue)}`
          : '';
        const query = `?pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}${searchPart}${folderPart}`;
        const res = await imageLibraryAdminApi.list(query);
        setImages(res.data?.data || []);
        setPagination({
          pageNumber,
          pageSize: PAGE_SIZE,
          totalRecords: res.data?.totalRecords || 0,
        });
      } catch {
        setImages([]);
        message.error('Failed to load image library');
      } finally {
        setLoading(false);
        setFirstLoad(false);
      }
    },
    [],
  );

  // Arama (debounce'lu) veya klasör değişince ilk sayfadan çek.
  useEffect(() => {
    fetchImages(1, debouncedSearch, folder);
  }, [fetchImages, debouncedSearch, folder]);

  const openCreate = () => setUpsertModal({ open: true, image: null });
  const openEdit = (image) => setUpsertModal({ open: true, image });
  const closeUpsert = () =>
    setUpsertModal((prev) => ({ ...prev, open: false }));

  const handleSaved = () => {
    fetchImages(pagination.pageNumber, debouncedSearch, folder);
    // Yeni klasör eklenmiş olabilir; pill listesini yenile.
    loadFolders();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await imageLibraryAdminApi.deleteById(deleteTarget.id);
      message.success('Image deleted');
      setDeleteTarget(null);
      fetchImages(pagination.pageNumber, debouncedSearch, folder);
      loadFolders();
    } catch (err) {
      const apiMessage =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Failed to delete image';
      message.error(apiMessage);
    } finally {
      setDeleting(false);
    }
  };

  // İlk paint: ilk yükleme tamamlanana kadar skeleton.
  if (firstLoad && loading) {
    return <ImageLibraryAdminSkeleton />;
  }

  const hasImages = images.length > 0;
  const showPagination = pagination.totalRecords > PAGE_SIZE;

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          title="Image Library"
          description="Manage the shared image catalog restaurants use for menu products."
          actions={
            <TWButton
              variant="primary"
              icon={PlusIcon}
              label="Add image"
              onClick={openCreate}
            />
          }
        />

        <div className="flex items-center gap-3">
          <div className="w-full max-w-sm">
            <TWInput
              leftIcon={MagnifyingGlassIcon}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or description…"
              aria-label="Search image library"
            />
          </div>
          {pagination.totalRecords > 0 ? (
            <span className="hidden shrink-0 text-xs font-semibold tabular-nums text-slate-400 dark:text-slate-500 sm:block">
              {pagination.totalRecords.toLocaleString('en-US')} photos
            </span>
          ) : null}
        </div>

        {folders.length > 0 ? (
          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
            <FolderPill
              label="All"
              active={folder === ''}
              onClick={() => setFolder('')}
            />
            {folders.map((f) => (
              <FolderPill
                key={f.folder}
                label={f.folder}
                count={f.imagecount}
                active={folder === f.folder}
                onClick={() => setFolder(f.folder)}
              />
            ))}
          </div>
        ) : null}

        {!hasImages ? (
          <EmptyState
            variant="dashed"
            title="No images yet"
            description={
              debouncedSearch || folder
                ? 'No images match your filters.'
                : 'Add an image to start building the shared catalog.'
            }
          />
        ) : (
          <div className="reveal space-y-4">
            <ImageLibraryGrid
              images={images}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
            />
            {showPagination ? (
              <div className="flex justify-end border-t border-slate-200 pt-3 dark:border-slate-700">
                <TWPagination
                  size="small"
                  current={pagination.pageNumber}
                  pageSize={PAGE_SIZE}
                  total={pagination.totalRecords}
                  showSizeChanger={false}
                  onChange={(page) =>
                    fetchImages(page, debouncedSearch, folder)
                  }
                />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <ImageLibraryUpsertModal
        open={upsertModal.open}
        image={upsertModal.image}
        onClose={closeUpsert}
        onSaved={handleSaved}
      />

      <TWConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        tone="danger"
        title="Delete library image"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" will be removed. Linked products fall back to their own uploaded image.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleting}
      />
    </div>
  );
};

export default ImageLibraryAdmin;
